import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { apiService, graphBuilderApi } from '../../services/api';
import { getGraphBuilderBaseUrl } from '../../utils/env';
import BuildGraphMenu from './BuildGraphMenu';
import TestGraphSettings from './TestGraphSettings';
import TestGraphChat from './TestGraphChat';
import ExecutionInsightsTab from './ExecutionInsightsTab';
import ExplainabilityDrawer from './ExplainabilityDrawer';
import SessionContextPanel from './SessionContextPanel';
import { LiveEvaluationProgress } from '../explainability';
import { exportChatMarkdown, exportChatJSON } from '../../utils/explainabilityExport';
import {
  loadTestGraphSettings,
  saveTestGraphSettings,
  buildPipelineRunPayload,
  resolveLlmProvider,
  applyEmbedForcedSettings,
} from '../../utils/pipelineRunPayload';
import { isEmbedMode } from '../../utils/embedAuth';
import './TestGraph.css';
import './ExplainabilityPage.css';
import '../process/chat/ChatAssistantPage.css';

// ---------------------------------------------------------------------------
// ExecutionInsight payload builder
//
// For STREAMING responses: the GraphBuilder publishes the full trace directly
// to Kafka, so this payload is lightweight (metadata only). The insight_id
// comes from the `complete` SSE event.
//
// For NON-STREAMING responses: the full trace is still included because the
// GraphBuilder doesn't publish it server-side for that path yet.
// ---------------------------------------------------------------------------

const INSIGHT_TRACE_HARD_CAP_BYTES = 130 * 1024 * 1024; // ~130 MB; below the nginx 150 MB

function _buildInsightPayload({
  graphId,
  workspaceId,
  query,
  data,
  streamedContent,
  settings,
  requestStartTime,
}) {
  const totalResponseTimeMs = Date.now() - requestStartTime;

  // If the GraphBuilder already published the insight (streaming path),
  // data.insight_id will be set and execution_trace may be absent.
  // In that case we only send lightweight metadata.
  const hasServerSideInsight = !!data.insight_id;

  const trace = data.execution_trace
    ? { ...data.execution_trace, total_response_time_ms: totalResponseTimeMs }
    : { total_response_time_ms: totalResponseTimeMs };

  if (!hasServerSideInsight && data.execution_trace) {
    let traceBytes = 0;
    try {
      traceBytes = new Blob([JSON.stringify(trace)]).size;
    } catch {
      traceBytes = 0;
    }
    if (traceBytes > INSIGHT_TRACE_HARD_CAP_BYTES) {
      throw new Error(
        `Trace too large to persist (${(traceBytes / 1_000_000).toFixed(1)} MB > ${(INSIGHT_TRACE_HARD_CAP_BYTES / 1_000_000).toFixed(0)} MB cap)`
      );
    }
  }

  return {
    graph_id: graphId,
    workspace_id: workspaceId || undefined,
    query,
    natural_language_answer: streamedContent || data.natural_language_answer,
    score: data.score,
    intent: data.metadata?.intent,
    execution_mode: data.metadata?.execution_mode,
    snn_execution_mode: data.metadata?.snn_execution_mode || settings.snn_execution_mode,
    llm_model: data.metadata?.llm_model || settings.llm_model,
    duration_ms: trace?.duration_ms,
    total_ticks: trace?.total_ticks,
    total_spikes: trace?.total_spikes,
    top_outcome_nodes: data.top_outcome_nodes || data.top_node_ids,
    // Only include heavy blobs for non-streaming (server publishes for streaming)
    execution_trace: hasServerSideInsight ? undefined : trace,
    prompt_expansions: hasServerSideInsight ? undefined : data.prompt_expansions,
    answer_metadata: data.metadata,
    llm_usage: data.llm_usage || null,
    rag_savings: data.rag_savings || null,
  };
}

// ---------------------------------------------------------------------------
// CacheStatusPill -- always-visible page-header indicator that surfaces the
// result of GET /api/cache/status/{graph_id} so the user immediately knows
// whether the graph's compiled NPZ + Brian preload pool are warm. Click
// scrolls to the cache controls inside Pipeline Settings (and expands the
// panel if it is collapsed). Hidden in non-compiled modes since cache is
// irrelevant there.
// ---------------------------------------------------------------------------
function CacheStatusPill({ info, loading, onClick }) {
  if (loading && !info) {
    return (
      <button type="button" className="cache-status-pill loading" disabled>
        <span className="cache-pill-dot" />
        Cache: ...
      </button>
    );
  }
  if (!info) {
    return null;
  }

  const compile = info.layers?.compile || {};
  const pool = info.layers?.pool || {};
  const dbCache = info.layers?.db_cache || {};

  const fullyCached = !!info.fully_cached;
  // Partial = at least one layer warm but not all. Disk artifacts OR an
  // in-memory pool count as partial warmth.
  const anyWarm =
    !!compile.compiled_full
    || !!compile.compiled_sharded
    || !!compile.has_synapse_embeddings
    || !!pool.loaded;

  let cls = 'uncached';
  let label = 'Not cached';
  if (fullyCached) {
    cls = 'cached';
    const nodeCount = pool.neurons ?? compile.node_count;
    const synCount = pool.synapses ?? compile.synapse_count;
    if (nodeCount != null && synCount != null) {
      label = `Cached - ${Number(nodeCount).toLocaleString()} nodes / ${Number(synCount).toLocaleString()} connections`;
    } else {
      label = 'Cached';
    }
  } else if (anyWarm) {
    cls = 'partial';
    label = 'Partially cached';
  }

  // Compose a tooltip listing layer-by-layer warmth so the user can
  // diagnose what's missing without leaving the page.
  const tooltipLines = ['Cache layers:'];
  if (compile.compiled_full !== undefined) {
    tooltipLines.push(`  compile.full: ${compile.compiled_full ? 'yes' : 'no'}`);
  }
  if (compile.compiled_sharded !== undefined) {
    tooltipLines.push(`  compile.sharded: ${compile.compiled_sharded ? 'yes' : 'no'}`);
  }
  if (compile.has_synapse_embeddings !== undefined) {
    tooltipLines.push(`  compile.synapse_embeddings: ${compile.has_synapse_embeddings ? 'yes' : 'no'}`);
  }
  if (compile.has_rate_coding_index !== undefined) {
    tooltipLines.push(`  compile.rate_coding_index: ${compile.has_rate_coding_index ? 'yes' : 'no'}`);
  }
  if (pool.loaded !== undefined) {
    tooltipLines.push(`  pool: ${pool.loaded ? 'loaded' : 'not loaded'}`);
  }
  const dbEntries =
    (dbCache.nodes_cached || 0)
    + (dbCache.synapses_cached || 0)
    + (dbCache.adjacency_cached || 0);
  if (dbEntries) {
    tooltipLines.push(`  db_cache.entries: ${dbEntries}`);
  }
  if (!fullyCached) {
    tooltipLines.push('');
    tooltipLines.push('Click to open cache controls.');
  }

  return (
    <button
      type="button"
      className={`cache-status-pill ${cls}`}
      onClick={onClick}
      title={tooltipLines.join('\n')}
    >
      <span className="cache-pill-dot" />
      {label}
    </button>
  );
}

function TestGraphPage({ assistantUuid = null, assistantPublicConfig = null } = {}) {
  const { graphId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Assistant mode: the page is mounted by the public /chatassist/[uuid] route.
  // There is no authenticated user, no graphId, and no settings panel; runs are
  // proxied through the server, which holds the locked snapshot + owner billing.
  const isAssistant = !!assistantUuid;
  
  // Graph state
  const [graph, setGraph] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Pipeline settings - initialize with graphId from URL
  const [settings, setSettings] = useState(() => {
    const loaded = loadTestGraphSettings(graphId);
    return isEmbedMode() ? applyEmbedForcedSettings(loaded) : loaded;
  });
  
  // Conversation state
  const [conversationHistory, setConversationHistory] = useState([]);
  const [messages, setMessages] = useState([]);
  const [lastSnnContext, setLastSnnContext] = useState(null);
  const processPollingRef = useRef({});

  // Session Context SNN state
  const [sessionContextEnabled, setSessionContextEnabled] = useState(() => {
    try {
      return localStorage.getItem('sessionContext_enabled') === 'true';
    } catch { return false; }
  });
  const [sessionContextSnapshot, setSessionContextSnapshot] = useState(null);
  const sessionIdRef = useRef(crypto.randomUUID());

  // Manual graph selection for published chat with router (empty = Auto Router)
  const [selectedGraphIds, setSelectedGraphIds] = useState(new Set());

  useEffect(() => {
    localStorage.setItem('sessionContext_enabled', String(sessionContextEnabled));
  }, [sessionContextEnabled]);
  
  // Settings panel state
  const [settingsCollapsed, setSettingsCollapsed] = useState(false);

  // Page-level cache status (mirrors what TestGraphSettings reads from
  // GET /api/cache/status/{graphId}). Lifted up so the header pill can
  // show "Cached / Partial / Not cached" at a glance without forcing the
  // user to expand the Pipeline Settings panel. The settings panel still
  // owns the in-flight Cache Graph job; it calls onCacheStateChange when
  // the job (or Delete Cache) finishes so this probe re-runs.
  const [cacheInfo, setCacheInfo] = useState(null);
  const [cacheProbeLoading, setCacheProbeLoading] = useState(false);

  const refreshCacheStatus = useCallback(async () => {
    if (!graphId) return;
    setCacheProbeLoading(true);
    try {
      const result = await graphBuilderApi.getCacheStatus(graphId);
      setCacheInfo(result || null);
    } catch (_err) {
      // Cache status is best-effort; on error just hide the pill.
      setCacheInfo(null);
    } finally {
      setCacheProbeLoading(false);
    }
  }, [graphId]);

  useEffect(() => {
    refreshCacheStatus();
  }, [refreshCacheStatus]);

  const isCompiledMode = useMemo(() => {
    const m = settings.snn_execution_mode;
    return m === 'compiled_full' || m === 'compiled_full_numpy' || m === 'compiled_sharded';
  }, [settings.snn_execution_mode]);

  const handleCachePillClick = useCallback(() => {
    setSettingsCollapsed(false);
    setTimeout(() => {
      const el = document.getElementById('cache-controls-row');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 50);
  }, []);

  // Tab state: 'chat' or 'insights'
  const [activeView, setActiveView] = useState('chat');
  
  // Focus insight ID - when set, the Execution Insights tab will auto-expand this insight
  const [focusInsightId, setFocusInsightId] = useState(null);

  // Explainability drawer state
  const [explainMessage, setExplainMessage] = useState(null);

  // Live progress state
  const [activeExecutionId, setActiveExecutionId] = useState(null);

  // Export dropdown state
  const [showExportMenu, setShowExportMenu] = useState(false);

  // Publish Chat Agent modal state
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [publishForm, setPublishForm] = useState({
    name: '', description: '', title: '', welcome_message: '', suggested_questions: '',
  });
  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState(null);
  const [publishResult, setPublishResult] = useState(null);

  // Close export dropdown on outside click
  useEffect(() => {
    if (!showExportMenu) return;
    const timer = setTimeout(() => {
      const handleClick = () => setShowExportMenu(false);
      document.addEventListener('click', handleClick, { once: true });
      return () => document.removeEventListener('click', handleClick);
    }, 0);
    return () => clearTimeout(timer);
  }, [showExportMenu]);

  // Load graph details
  const loadGraphDetails = useCallback(async () => {
    // In assistant mode there is no authenticated graph fetch (and no graphId);
    // the proxy owns the configuration, so skip straight to the chat.
    if (isAssistant) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const response = await apiService.getGraphDetails(graphId);
      if (response.success) {
        setGraph(response.graph);
        // Auto-populate workspace_id when graph loads
        if (response.graph?.workspaceId) {
          setSettings(prev => {
            const next = {
              ...prev,
              workspace_id: response.graph.workspaceId,
              graph_id: graphId,
            };
            return isEmbedMode() ? applyEmbedForcedSettings(next) : next;
          });
        }
      } else {
        setError(response.message || 'Failed to load graph details');
      }
    } catch (err) {
      console.error('Error loading graph:', err);
      setError('Failed to load graph details');
    } finally {
      setLoading(false);
    }
  }, [graphId, isAssistant]);

  // Load graph on mount
  useEffect(() => {
    loadGraphDetails();
  }, [loadGraphDetails]);

  // Handle incoming process execution from Process Execution Page
  useEffect(() => {
    if (location.state?.processExecution) {
      const execution = location.state.processExecution;
      
      // Calculate metrics
      let totalTokens = 0;
      let durationMs = 0;
      if (execution.node_results) {
        Object.values(execution.node_results).forEach(result => {
          totalTokens += result.tokens_used || 0;
        });
      }
      if (execution.started_at && execution.completed_at) {
        durationMs = new Date(execution.completed_at) - new Date(execution.started_at);
      }
      
      // Add process result as a message
      const processMessage = {
        id: Date.now(),
        type: 'process_assistant',
        content: 'Process completed successfully',
        timestamp: new Date().toISOString(),
        processData: {
          ...execution,
          total_tokens: totalTokens,
          duration_ms: durationMs,
        },
      };
      
      setMessages(prev => [...prev, processMessage]);
      
      // Clear the state to prevent re-adding on navigation
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Persist settings to localStorage when they change.
  // Skip in embed mode so the forced overlay does not overwrite the CM
  // direct-UI user's stored settings for the same graph.
  useEffect(() => {
    if (isEmbedMode() || isAssistant || !graphId) return;
    saveTestGraphSettings(graphId, settings);
  }, [settings, graphId, isAssistant]);

  // Handle settings change
  const handleSettingsChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  // Handle sending a message
  // overrides: optional {graph_id, skip_router} for re-execution after ambiguous routing
  const handleSendMessage = async (userQuery, overrides) => {
    // Trim conversation history to last 10 messages (5 turns) and strip to role+content only
    const trimmedHistory = conversationHistory.slice(-10).map(({ role, content }) => ({ role, content }));
    
    // Add user message to UI immediately
    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: userQuery,
      timestamp: new Date().toISOString(),
    };
    setMessages(prev => [...prev, userMessage]);

    // In assistant mode the snapshot config + billing live server-side; we only
    // send the per-turn fields and let the proxy rebuild the pipeline request.
    // Merge manual graph selection into overrides when the user picks specific graphs.
    let effectiveOverrides = overrides;
    if (isAssistant && selectedGraphIds.size > 0 && !overrides) {
      if (selectedGraphIds.size === 1) {
        const [singleId] = selectedGraphIds;
        effectiveOverrides = { skip_router: true, graph_id: singleId };
      } else {
        effectiveOverrides = { target_graph_ids: [...selectedGraphIds] };
      }
    }
    const assistantBody = isAssistant ? {
      nl_query: userQuery,
      conversation_history: trimmedHistory,
      last_snn_context: lastSnnContext,
      overrides: effectiveOverrides,
    } : null;

    let payload = null;
    if (!isAssistant) {
      // Resolve provider_id from the model selector -- API key stays server-side.
      // DB-only mode requires every selection to be in the provider:: form.
      if (settings.llm_model && !settings.llm_model.startsWith('provider::')) {
        console.error('[LLM-TELEMETRY] Non-provider model selected; DB-only mode requires a provider:: model');
        setMessages(prev => [...prev, {
          id: Date.now() + 1, type: 'error',
          content: 'Please select a DB-configured LLM provider from the settings panel.',
          timestamp: new Date().toISOString(),
        }]);
        return;
      }
      const { providerId, rawModel: effectiveLlmModel } = resolveLlmProvider(settings);

      payload = buildPipelineRunPayload({
        settings,
        nl_query: userQuery,
        graph_id: settings.graph_id,
        workspace_id: settings.workspace_id,
        providerId,
        effectiveLlmModel,
        conversation_history: trimmedHistory,
        last_snn_context: lastSnnContext,
        session_id: sessionContextEnabled ? sessionIdRef.current : undefined,
        overrides,
      });
    }

    const requestStartTime = Date.now();

    // Backend supports per-graph streaming for multi-graph execution.
    const useStream = isAssistant
      ? (assistantPublicConfig?.stream !== false)
      : settings.stream_response;

    // Use streaming or non-streaming based on setting
    if (useStream) {
      // STREAMING MODE
      const streamingMessageId = Date.now() + 1;
      let streamedContent = '';
      let streamMetadata = null;
      
      // Add streaming message placeholder
      const streamingMessage = {
        id: streamingMessageId,
        type: 'streaming',
        content: '',
        phase: 'Connecting...',
        timestamp: new Date().toISOString(),
      };
      setMessages(prev => [...prev, streamingMessage]);

      try {
        const streamCallbacks = {
          onProgress: (data) => {
            if (data.execution_id || data.query_run_id) {
              setActiveExecutionId(data.execution_id || data.query_run_id);
            }
            setMessages(prev => prev.map(m => 
              m.id === streamingMessageId 
                ? { ...m, phase: data.message || data.phase }
                : m
            ));
          },
          onToken: (token) => {
            streamedContent += token;
            setMessages(prev => prev.map(m => 
              m.id === streamingMessageId 
                ? { ...m, content: streamedContent, type: 'streaming' }
                : m
            ));
          },
          onTokenReplace: (newContent) => {
            streamedContent = newContent;
            setMessages(prev => prev.map(m =>
              m.id === streamingMessageId
                ? { ...m, content: newContent, type: 'streaming' }
                : m
            ));
          },
          onRefinedAnswer: (newContent) => {
            streamedContent = newContent;
            setMessages(prev => prev.map(m =>
              m.id === streamingMessageId
                ? { ...m, content: newContent, type: 'streaming' }
                : m
            ));
          },
          onGraphStart: (data) => {
            const placeholderId = `graph_${data.graph_id}`;
            setMessages(prev => {
              if (prev.some(m => m.id === placeholderId)) return prev;
              return [...prev, {
                id: placeholderId,
                type: 'streaming',
                content: '',
                phase: `Querying ${data.graph_name || data.graph_id?.substring(0, 8)}...`,
                graphId: data.graph_id,
                graphName: data.graph_name || '',
                timestamp: new Date().toISOString(),
              }];
            });
          },
          onGraphProgress: (data) => {
            const placeholderId = `graph_${data.graph_id}`;
            setMessages(prev => prev.map(m =>
              m.id === placeholderId
                ? { ...m, phase: data.message || data.phase || m.phase }
                : m
            ));
          },
          onGraphToken: (data) => {
            const placeholderId = `graph_${data.graph_id}`;
            setMessages(prev => prev.map(m =>
              m.id === placeholderId
                ? { ...m, content: (m.content || '') + data.content, type: 'streaming' }
                : m
            ));
          },
          onGraphTokenReplace: (data) => {
            const placeholderId = `graph_${data.graph_id}`;
            setMessages(prev => prev.map(m =>
              m.id === placeholderId
                ? { ...m, content: data.content, type: 'streaming' }
                : m
            ));
          },
          onGraphComplete: (data) => {
            const placeholderId = `graph_${data.graph_id}`;
            setMessages(prev => prev.map(m =>
              m.id === placeholderId
                ? {
                    ...m,
                    type: 'assistant',
                    content: data.natural_language_answer || m.content || 'No response received.',
                    metadata: {
                      score: data.score,
                      top_outcome_nodes: data.top_outcome_nodes || [],
                      execution_trace: data.execution_trace || null,
                      citation_index: data.citation_index || data.execution_trace?.citation_index || null,
                      llm_usage: data.llm_usage || null,
                      rag_savings: data.rag_savings || null,
                      query_run_id: data.metadata?.query_run_id || null,
                      multi_graph_source: {
                        graph_id: data.graph_id,
                        graph_name: data.graph_name,
                        routing_score: data.routing_score,
                      },
                    },
                  }
                : m
            ));
          },
          onComplete: async (data) => {
            setActiveExecutionId(null);
            streamMetadata = data;
            const isConversational = data.metadata?.routing === 'CONVERSATIONAL';

            // Inject process enrichment results as a separate message if present
            if (data.metadata?.process_enrichment_results) {
              const sEnrichMsg = {
                id: Date.now() + 1,
                type: 'process_enrichment',
                content: '',
                enrichmentData: data.metadata.process_enrichment_results,
                enrichmentCaseJson: data.metadata.enrichment_case_json || null,
                timestamp: new Date().toISOString(),
              };
              setMessages(prev => [...prev, sEnrichMsg]);
            }

            // Check for process polling response
            if (data.metadata?.needs_process_poll) {
              handleProcessPollResponse(streamingMessageId, data.metadata);
              return;
            }

            // Per-graph streaming: graph_complete events already created messages.
            // Check if any graph_* messages exist -- if so, just clean up the
            // original streaming placeholder and update conversation history.
            const hasPerGraphMsgs = await new Promise(resolve => {
              setMessages(prev => {
                resolve(prev.some(m => typeof m.id === 'string' && m.id.startsWith('graph_')));
                return prev;
              });
            });
            if (hasPerGraphMsgs) {
              setMessages(prev => prev.filter(m => m.id !== streamingMessageId));
              setConversationHistory(prev => [
                ...prev,
                { role: 'user', content: userQuery },
                { role: 'assistant', content: data.natural_language_answer || '', nodes: [] },
              ]);
              return;
            }

            // Legacy fallback: multi_graph_results in one batch (non-per-graph backend)
            const streamMultiResults = data.metadata?.multi_graph_results;
            if (streamMultiResults && streamMultiResults.length > 0) {
              const mgId = Date.now();
              const mgMessages = streamMultiResults.map((gr, idx) => ({
                id: mgId + idx + 2,
                type: 'assistant',
                content: gr.natural_language_answer || 'No response received.',
                metadata: {
                  score: gr.score,
                  top_outcome_nodes: gr.top_outcome_nodes || [],
                  execution_trace: gr.execution_trace || null,
                  citation_index: gr.citation_index || gr.execution_trace?.citation_index || null,
                  llm_usage: gr.llm_usage || null,
                  rag_savings: gr.rag_savings || null,
                  query_run_id: gr.metadata?.query_run_id || null,
                  routing: data.metadata?.routing,
                  multi_graph_source: {
                    graph_id: gr.graph_id,
                    graph_name: gr.graph_name,
                    routing_score: gr.routing_score,
                  },
                  multi_graph_group: mgId,
                  multi_graph_total: streamMultiResults.length,
                  multi_graph_index: idx,
                },
                timestamp: new Date().toISOString(),
              }));
              setMessages(prev => [...prev.filter(m => m.id !== streamingMessageId), ...mgMessages]);
              const best = streamMultiResults.reduce((a, b) => (a.score > b.score ? a : b), streamMultiResults[0]);
              setConversationHistory(prev => [
                ...prev,
                { role: 'user', content: userQuery },
                { role: 'assistant', content: best.natural_language_answer, nodes: best.top_outcome_nodes || [] },
              ]);
              return;
            }
            
            // Convert streaming message to final assistant message — aligned with non-streaming metadata
            setMessages(prev => prev.map(m => 
              m.id === streamingMessageId 
                ? { 
                    ...m, 
                    type: 'assistant',
                    content: streamedContent || data.natural_language_answer || 'No response received.',
                    phase: undefined,
                    metadata: {
                      score: data.score,
                      top_outcome_nodes: data.top_outcome_nodes || data.top_node_ids,
                      intent: data.metadata?.intent,
                      execution_mode: data.metadata?.execution_mode,
                      prompt_expansions: data.prompt_expansions,
                      execution_trace: data.execution_trace,
                      citation_index: data.citation_index || data.execution_trace?.citation_index || null,
                      llm_model: data.metadata?.llm_model,
                      stage_models: data.metadata?.stage_models,
                      snn_execution_mode: data.metadata?.snn_execution_mode,
                      routing: data.metadata?.routing,
                      needs_user_selection: data.metadata?.needs_user_selection || false,
                      original_query: data.metadata?.original_query,
                      llm_usage: data.llm_usage || null,
                      rag_savings: data.rag_savings || null,
                      query_run_id: data.metadata?.query_run_id || null,
                    },
                  }
                : m
            ));
            
            // Update conversation history
            setConversationHistory(prev => [
              ...prev,
              { role: 'user', content: userQuery },
              { 
                role: 'assistant', 
                content: streamedContent,
                nodes: data.top_outcome_nodes || data.top_node_ids,
              },
            ]);

            // Update last Platform context only for graph query responses (not conversational)
            if (!isConversational) {
              setLastSnnContext({
                answer: streamedContent || data.natural_language_answer,
                score: data.score,
                top_outcome_nodes: data.top_outcome_nodes || data.top_node_ids || [],
                intent: data.metadata?.intent,
              });
            }

            // Session Context SNN: process the user's turn in parallel (fire-and-forget)
            if (sessionContextEnabled && !isAssistant) {
              graphBuilderApi.sessionContextProcessTurn(sessionIdRef.current, userQuery)
                .then(snap => setSessionContextSnapshot(snap))
                .catch(err => console.warn('Session context engine error (stream):', err));
            }

            // Only persist execution insight for Platform responses (not conversational).
            // Skipped in assistant mode: the public caller has no CM account/auth.
            if (!isConversational && !isAssistant) {
              // Streaming path: GraphBuilder publishes the insight server-side
              // and returns insight_id in the complete event. Use it directly.
              if (data.insight_id) {
                setMessages(prev => prev.map(m =>
                  m.id === streamingMessageId
                    ? { ...m, metadata: { ...m.metadata, insightId: data.insight_id } }
                    : m
                ));
              } else {
                // Fallback: server didn't provide insight_id, POST lightweight payload
                const insightPayload = _buildInsightPayload({
                  graphId,
                  workspaceId: settings.workspace_id,
                  query: userQuery,
                  data,
                  streamedContent,
                  settings,
                  requestStartTime,
                });
                apiService.saveExecutionInsight(insightPayload)
                  .then(saveResult => {
                    if (saveResult?.insight_id) {
                      setMessages(prev => prev.map(m =>
                        m.id === streamingMessageId
                          ? { ...m, metadata: { ...m.metadata, insightId: saveResult.insight_id } }
                          : m
                      ));
                    }
                  })
                  .catch(saveErr => {
                    console.warn('Failed to queue execution insight (streaming):', saveErr);
                    const _status = saveErr?.response?.status;
                    const _msg = _status === 413
                      ? 'Trace too large to save (413)'
                      : (saveErr?.response?.data?.error || saveErr?.message || 'unknown');
                    setMessages(prev => prev.map(m =>
                      m.id === streamingMessageId
                        ? { ...m, metadata: { ...m.metadata, insightSaveError: _msg } }
                        : m
                    ));
                  });
              }
            }
          },
          onError: (error) => {
            setActiveExecutionId(null);
            setMessages(prev => prev.map(m => 
              m.id === streamingMessageId 
                ? { 
                    ...m, 
                    type: 'error',
                    content: error.message || 'An error occurred during streaming.',
                    phase: undefined,
                  }
                : m
            ));
          },
        };

        if (isAssistant) {
          await graphBuilderApi.runChatAssistantStream(assistantUuid, assistantBody, streamCallbacks);
        } else {
          await graphBuilderApi.runPipelineStream(payload, streamCallbacks);
        }
      } catch (err) {
        console.error('Streaming pipeline error:', err);
        // Error already handled in onError callback
      }
    } else {
      // NON-STREAMING MODE (original behavior)
      // Add loading message
      const loadingMessage = {
        id: Date.now() + 1,
        type: 'loading',
        content: 'Processing...',
        timestamp: new Date().toISOString(),
      };
      setMessages(prev => [...prev, loadingMessage]);

      try {
        const response = isAssistant
          ? await graphBuilderApi.runChatAssistant(assistantUuid, assistantBody)
          : await graphBuilderApi.runPipeline(payload);
        const isConversational = response.metadata?.routing === 'CONVERSATIONAL';
        
        // Remove loading message
        setMessages(prev => prev.filter(m => m.type !== 'loading'));

        // Inject process enrichment results as a separate message if present
        if (response.metadata?.process_enrichment_results) {
          const enrichMsg = {
            id: Date.now() + 1,
            type: 'process_enrichment',
            content: '',
            enrichmentData: response.metadata.process_enrichment_results,
            enrichmentCaseJson: response.metadata.enrichment_case_json || null,
            timestamp: new Date().toISOString(),
          };
          setMessages(prev => [...prev, enrichMsg]);
        }

        // Check for process polling response
        if (response.metadata?.needs_process_poll) {
          const procMsgId = Date.now() + 2;
          const procMsg = {
            id: procMsgId,
            type: 'assistant',
            content: response.natural_language_answer || 'Process execution started...',
            metadata: response.metadata,
            timestamp: new Date().toISOString(),
          };
          setMessages(prev => [...prev, procMsg]);
          handleProcessPollResponse(procMsgId, response.metadata);
          return;
        }

        // Multi-graph results: create one assistant message per graph
        const multiResults = response.metadata?.multi_graph_results;
        if (multiResults && multiResults.length > 0) {
          const multiGroupId = Date.now();
          const multiMessages = multiResults.map((gr, idx) => ({
            id: multiGroupId + idx + 2,
            type: 'assistant',
            content: gr.natural_language_answer || 'No response received.',
            metadata: {
              score: gr.score,
              top_outcome_nodes: gr.top_outcome_nodes || [],
              execution_trace: gr.execution_trace || null,
              citation_index: gr.citation_index || gr.execution_trace?.citation_index || null,
              llm_usage: gr.llm_usage || null,
              rag_savings: gr.rag_savings || null,
              query_run_id: gr.metadata?.query_run_id || null,
              routing: response.metadata?.routing,
              multi_graph_source: {
                graph_id: gr.graph_id,
                graph_name: gr.graph_name,
                routing_score: gr.routing_score,
              },
              multi_graph_group: multiGroupId,
              multi_graph_total: multiResults.length,
              multi_graph_index: idx,
            },
            timestamp: new Date().toISOString(),
          }));
          setMessages(prev => [...prev, ...multiMessages]);

          const bestResult = multiResults.reduce((a, b) => (a.score > b.score ? a : b), multiResults[0]);
          setConversationHistory(prev => [
            ...prev,
            { role: 'user', content: userQuery },
            { role: 'assistant', content: bestResult.natural_language_answer, nodes: bestResult.top_outcome_nodes || [] },
          ]);
          setLastSnnContext({
            answer: bestResult.natural_language_answer,
            score: bestResult.score,
            top_outcome_nodes: bestResult.top_outcome_nodes || [],
          });

          if (!isAssistant) {
            for (const [idx, gr] of multiResults.entries()) {
              const insightPayload = _buildInsightPayload({
                graphId: gr.graph_id || graphId,
                workspaceId: settings.workspace_id,
                query: userQuery,
                data: gr,
                streamedContent: gr.natural_language_answer,
                settings,
                requestStartTime,
              });
              const msgId = multiGroupId + idx + 2;
              apiService.saveExecutionInsight(insightPayload)
                .then(saveResult => {
                  if (saveResult?.insight_id) {
                    setMessages(prev => prev.map(m =>
                      m.id === msgId ? { ...m, metadata: { ...m.metadata, insightId: saveResult.insight_id } } : m
                    ));
                  }
                })
                .catch(saveErr => {
                  console.warn(`Failed to queue insight for graph ${gr.graph_name}:`, saveErr);
                });
            }
          }
          return;
        }
        
        // Add assistant response
        const assistantMessage = {
          id: Date.now() + 2,
          type: 'assistant',
          content: response.natural_language_answer || 'No response received.',
          metadata: {
            score: response.score,
            top_outcome_nodes: response.top_outcome_nodes,
            intent: response.metadata?.intent,
            execution_mode: response.metadata?.execution_mode,
            prompt_expansions: response.prompt_expansions,
            execution_trace: response.execution_trace,
            citation_index: response.citation_index || response.execution_trace?.citation_index || null,
            routing: response.metadata?.routing,
            needs_user_selection: response.metadata?.needs_user_selection || false,
            original_query: response.metadata?.original_query,
            llm_usage: response.llm_usage || null,
            rag_savings: response.rag_savings || null,
            query_run_id: response.metadata?.query_run_id || null,
          },
          timestamp: new Date().toISOString(),
        };
        setMessages(prev => [...prev, assistantMessage]);
        
        // Update conversation history for context
        setConversationHistory(prev => [
          ...prev,
          { role: 'user', content: userQuery },
          { 
            role: 'assistant', 
            content: response.natural_language_answer,
            nodes: response.top_outcome_nodes,
          },
        ]);

        // Update last Platform context only for graph query responses (not conversational)
        if (!isConversational) {
          setLastSnnContext({
            answer: response.natural_language_answer,
            score: response.score,
            top_outcome_nodes: response.top_outcome_nodes || [],
            intent: response.metadata?.intent,
          });
        }

        // Session Context SNN: process the user's turn in parallel (fire-and-forget)
        if (sessionContextEnabled && !isAssistant) {
          graphBuilderApi.sessionContextProcessTurn(sessionIdRef.current, userQuery)
            .then(snap => setSessionContextSnapshot(snap))
            .catch(err => console.warn('Session context engine error:', err));
        }

        // Only persist execution insight for Platform responses (not conversational).
        // Skipped in assistant mode: the public caller has no CM account/auth.
        if (!isConversational && !isAssistant) {
          const insightPayload = _buildInsightPayload({
            graphId,
            workspaceId: settings.workspace_id,
            query: userQuery,
            data: response,
            streamedContent: response.natural_language_answer,
            settings,
            requestStartTime,
          });
          // Fire-and-forget: backend returns 202 with pre-generated insight_id
          apiService.saveExecutionInsight(insightPayload)
            .then(saveResult => {
              if (saveResult?.insight_id) {
                setMessages(prev => prev.map(m =>
                  m.id === assistantMessage.id
                    ? { ...m, metadata: { ...m.metadata, insightId: saveResult.insight_id } }
                    : m
                ));
              }
            })
            .catch(saveErr => {
              console.warn('Failed to queue execution insight:', saveErr);
              const _status = saveErr?.response?.status;
              const _msg = _status === 413
                ? 'Trace too large to save (413)'
                : (saveErr?.response?.data?.error || saveErr?.message || 'unknown');
              setMessages(prev => prev.map(m =>
                m.id === assistantMessage.id
                  ? { ...m, metadata: { ...m.metadata, insightSaveError: _msg } }
                  : m
              ));
            });
        }
        
      } catch (err) {
        console.error('Pipeline error:', err);
        
        // Remove loading message
        setMessages(prev => prev.filter(m => m.type !== 'loading'));
        
        // Safely extract error detail (may be string or Pydantic validation error array)
        let errorDetail = err.response?.data?.detail;
        if (errorDetail && typeof errorDetail !== 'string') {
          errorDetail = JSON.stringify(errorDetail);
        }
        
        // Add error message
        const errorMessage = {
          id: Date.now() + 2,
          type: 'error',
          content: errorDetail || err.message || 'An error occurred while processing your request.',
          timestamp: new Date().toISOString(),
        };
        setMessages(prev => [...prev, errorMessage]);
      }
    }
  };

  // Clear conversation context
  const handleClearContext = () => {
    setConversationHistory([]);
    setMessages([]);
    setLastSnnContext(null);
    // Clear session context SNN
    if (sessionContextEnabled) {
      graphBuilderApi.sessionContextClear(sessionIdRef.current).catch(() => {});
      setSessionContextSnapshot(null);
      sessionIdRef.current = crypto.randomUUID();
    }
  };

  // Handle "Execution Insight" link click - switch to insights tab and focus the insight
  const handleViewInsight = (insightId) => {
    setFocusInsightId(insightId);
    setActiveView('insights');
  };

  // Handle user selecting a target from the ambiguous-routing selection card
  const handleRouterGraphSelection = async (messageId, selectedId, selectedName, selectedTargetType) => {
    // Mark the selection message as confirmed
    setMessages(prev => prev.map(m =>
      m.id === messageId
        ? {
            ...m,
            metadata: {
              ...m.metadata,
              _selectionConfirmed: true,
              _selectedGraphName: selectedName,
            },
          }
        : m
    ));

    const originalQuery = messages.find(m => m.id === messageId)?.metadata?.original_query;
    const routerMode = settings.router_mode || 'route_and_execute';
    const isProcess = selectedTargetType === 'process';

    if (routerMode === 'route_only') {
      const confirmMsg = {
        id: Date.now() + 10,
        type: 'assistant',
        content: `Routed to${isProcess ? ' Process' : ''}: ${selectedName} (user selected)`,
        metadata: {
          routing: {
            target_graph_id: selectedId,
            target_graph_name: selectedName,
            target_type: selectedTargetType || 'graph',
            confidence: 1.0,
            ambiguous: false,
            user_selected: true,
          },
        },
        timestamp: new Date().toISOString(),
      };
      setMessages(prev => [...prev, confirmMsg]);
      return;
    }

    // For route_and_execute, re-execute with the selected target
    if (originalQuery) {
      if (isProcess) {
        await handleSendMessage(originalQuery, { process_id: selectedId, target_type: 'process', skip_router: true });
      } else {
        await handleSendMessage(originalQuery, { graph_id: selectedId, skip_router: true });
      }
    }
  };

  // Start polling for process execution status
  const startProcessPolling = useCallback((messageId, executionId, processName) => {
    const POLL_HARD_TIMEOUT_MS = 10 * 60 * 1000;
    const pollStartTime = Date.now();
    let delay = 2000;

    const poll = async () => {
      if (!processPollingRef.current[messageId]) return;
      if (Date.now() - pollStartTime > POLL_HARD_TIMEOUT_MS) {
        setMessages(prev => prev.map(m =>
          m.id === messageId
            ? {
                ...m,
                type: 'error',
                content: `Process "${processName}" timed out after 10 minutes.`,
              }
            : m
        ));
        delete processPollingRef.current[messageId];
        return;
      }

      try {
        const status = await graphBuilderApi.getProcessStatus(executionId);
        const completedNodes = (status.completed_nodes || []).length;
        const activeNodes = (status.active_nodes || []).length;
        const pendingNodes = (status.pending_nodes || []).length;
        const totalNodes = completedNodes + activeNodes + pendingNodes;

        // Update progress on the message
        setMessages(prev => prev.map(m =>
          m.id === messageId && m.type === 'process_polling'
            ? {
                ...m,
                processPollingData: {
                  ...m.processPollingData,
                  status: status.status,
                  completedNodes,
                  totalNodes,
                  activeNodes,
                  progress: status.progress || 0,
                },
              }
            : m
        ));

        if (status.status === 'completed') {
          const outputs = status.final_outputs || {};
          let answer = outputs.natural_language_answer;
          if (!answer) {
            const stringOutputs = Object.entries(outputs)
              .filter(([, v]) => typeof v === 'string')
              .map(([k, v]) => `**${k}:** ${v}`);
            answer = stringOutputs.length > 0
              ? stringOutputs.join('\n\n')
              : `Process completed with ${Object.keys(outputs).length} output(s).`;
          }
          setMessages(prev => prev.map(m =>
            m.id === messageId
              ? {
                  ...m,
                  type: 'assistant',
                  content: answer,
                  processPollingData: undefined,
                  metadata: {
                    ...m.metadata,
                    score: outputs.score || 0,
                    top_outcome_nodes: outputs.top_outcome_nodes || [],
                    execution_type: 'process',
                    process_execution_id: executionId,
                    process_outputs: outputs,
                  },
                }
              : m
          ));
          delete processPollingRef.current[messageId];
          return;
        }

        if (status.status === 'failed' || status.status === 'cancelled') {
          setMessages(prev => prev.map(m =>
            m.id === messageId
              ? {
                  ...m,
                  type: 'error',
                  content: `Process ${status.status}: ${status.error || 'Unknown error'}`,
                  processPollingData: undefined,
                }
              : m
          ));
          delete processPollingRef.current[messageId];
          return;
        }

        // Exponential backoff: 2s, 3s, 5s, 8s, max 10s
        delay = Math.min(delay * 1.5, 10000);
        processPollingRef.current[messageId] = setTimeout(poll, delay);
      } catch (pollErr) {
        console.error('Process polling error:', pollErr);
        delay = Math.min(delay * 2, 10000);
        processPollingRef.current[messageId] = setTimeout(poll, delay);
      }
    };

    processPollingRef.current[messageId] = setTimeout(poll, delay);
  }, []);

  // Helper to handle needs_process_poll response
  const handleProcessPollResponse = useCallback((messageId, metadata) => {
    if (!metadata?.needs_process_poll || !metadata?.process_execution_id) return false;

    setMessages(prev => prev.map(m =>
      m.id === messageId
        ? {
            ...m,
            type: 'process_polling',
            content: `Executing process: ${metadata.process_name || 'Process'}...`,
            processPollingData: {
              executionId: metadata.process_execution_id,
              processName: metadata.process_name || 'Process',
              status: 'running',
              completedNodes: 0,
              totalNodes: 0,
              activeNodes: 0,
            },
          }
        : m
    ));

    startProcessPolling(messageId, metadata.process_execution_id, metadata.process_name || 'Process');
    return true;
  }, [startProcessPolling]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      Object.values(processPollingRef.current).forEach(timer => clearTimeout(timer));
      processPollingRef.current = {};
    };
  }, []);

  // Export helpers
  const downloadBlob = (content, filename, mimeType) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const openPublishModal = () => {
    setPublishError(null);
    setPublishResult(null);
    setPublishForm({
      name: graph?.graphName ? `${graph.graphName} Assistant` : 'Chat Assistant',
      description: '',
      title: graph?.graphName || 'Chat Assistant',
      welcome_message: '',
      suggested_questions: '',
    });
    setShowPublishModal(true);
  };

  const handlePublishChatAgent = async () => {
    try {
      setPublishing(true);
      setPublishError(null);

      // Resolve the publisher account the same way the GraphBuilder axios
      // interceptor derives X-CM-Account-Id (see services/api.js).
      let accountId = null;
      try {
        const userRaw = localStorage.getItem('userData') || localStorage.getItem('user');
        if (userRaw) {
          const u = JSON.parse(userRaw);
          accountId = u?.account_id || u?.AccountId || u?.accountId || null;
        }
      } catch (_) { /* ignore */ }

      // Build a server-ready base pipeline payload from the current settings,
      // then strip the per-turn fields so the proxy can merge them per request.
      const { providerId, rawModel: effectiveLlmModel } = resolveLlmProvider(settings);
      const basePayload = buildPipelineRunPayload({
        settings,
        nl_query: '',
        graph_id: settings.graph_id,
        workspace_id: settings.workspace_id,
        providerId,
        effectiveLlmModel,
      });
      delete basePayload.nl_query;
      delete basePayload.conversation_history;
      delete basePayload.last_snn_context;
      delete basePayload.session_id;

      const suggested_questions = (publishForm.suggested_questions || '')
        .split('\n').map((s) => s.trim()).filter(Boolean);

      // Snapshot router targets so the public page can show a graph selector
      let routerTargets = null;
      if (settings.router_graph_id) {
        try {
          const rt = await apiService.getRouterTargets(settings.router_graph_id);
          routerTargets = (rt.targets || []).map((t) => ({
            target_graph_id: t.target_graph_id || t.target_process_id,
            target_name: t.target_name,
            target_type: t.target_type || 'graph',
          }));
        } catch (_) { /* non-critical */ }
      }

      const config = {
        pipeline_payload: basePayload,
        settings,
        router_graph_id: settings.router_graph_id || null,
        router_mode: settings.router_mode || null,
        multi_graph_enabled: !!settings.multi_graph_enabled,
        multi_graph_threshold_pct: settings.multi_graph_threshold_pct ?? 30,
        router_targets: routerTargets,
        pre_enrich_process_ids: settings.pre_enrich_process_ids || null,
        enable_nl_to_json: !!settings.enable_nl_to_json,
        title: publishForm.title || null,
        welcome_message: publishForm.welcome_message || null,
        suggested_questions,
        stream: settings.stream_response !== false,
      };

      const resp = await graphBuilderApi.createChatAssistant({
        name: publishForm.name,
        description: publishForm.description,
        graph_id: settings.graph_id,
        workspace_id: settings.workspace_id,
        account_id: accountId,
        config,
      });

      const uuid = resp.chat_assistant_id;
      setPublishResult({
        uuid,
        url: `${window.location.origin}/chatassist/${uuid}`,
      });
    } catch (err) {
      console.error('Error publishing chat agent:', err);
      const detail = err.response?.data?.detail;
      setPublishError((typeof detail === 'string' ? detail : null) || err.message || 'Failed to publish chat agent');
    } finally {
      setPublishing(false);
    }
  };

  const handleExportJSON = () => {
    const json = exportChatJSON(messages, graphId, settings, graph?.graphName);
    downloadBlob(json, `chat-${graphId.substring(0, 8)}-${Date.now()}.json`, 'application/json');
    setShowExportMenu(false);
  };

  const handleExportMarkdown = () => {
    const md = exportChatMarkdown(messages, graphId, settings, graph?.graphName);
    downloadBlob(md, `chat-${graphId.substring(0, 8)}-${Date.now()}.md`, 'text/markdown');
    setShowExportMenu(false);
  };

  if (loading) {
    return (
      <div className="build-graph-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading graph...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="build-graph-page">
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button onClick={() => navigate('/user-home/graphs')}>
            Back to Graphs
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="build-graph-page test-graph-page">
      {/* Navigation Menu */}
      <BuildGraphMenu graphId={graphId} graphName={graph?.graphName} workspaceId={settings.workspace_id} />
      
      {/* Page Header */}
      {!isAssistant && (
      <div className="test-graph-header">
        <div className="test-graph-title">
          <h2>Test Graph</h2>
          <span className="graph-info">
            {graph?.graphName || 'Unnamed Graph'} ({graphId ? `${graphId.substring(0, 8)}...` : ''})
          </span>
          {isCompiledMode && (
            <CacheStatusPill
              info={cacheInfo}
              loading={cacheProbeLoading}
              onClick={handleCachePillClick}
            />
          )}
        </div>
        <div className="test-graph-actions">
          {!isEmbedMode() && (
            <button
              className="test-graph-publish-btn"
              onClick={openPublishModal}
              title="Publish this configuration as a public Chat Assistant"
            >
              🚀 Publish Chat Agent
            </button>
          )}
          <label className="session-context-toggle" title="Enable Session Context Engine (LLM-free context tracking)">
            <input
              type="checkbox"
              checked={sessionContextEnabled}
              onChange={(e) => setSessionContextEnabled(e.target.checked)}
            />
            <span className="session-context-toggle-label">Context Engine</span>
          </label>
          <button 
            className="btn-secondary"
            onClick={handleClearContext}
            disabled={messages.length === 0}
          >
            Clear Context
          </button>
          <div className="ex-export-dropdown-wrap">
            <button 
              className="btn-secondary"
              onClick={() => setShowExportMenu(prev => !prev)}
              disabled={messages.length === 0}
            >
              Export Chat &#9662;
            </button>
            {showExportMenu && (
              <div className="ex-export-dropdown">
                <button className="ex-export-option" onClick={handleExportMarkdown}>
                  Markdown (.md) &mdash; with explainability
                </button>
                <button className="ex-export-option" onClick={handleExportJSON}>
                  JSON (.json) &mdash; raw data
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      )}

      {/* View Tab Bar */}
      {!isAssistant && (
      <div className="test-graph-view-tabs">
        <button
          type="button"
          className={`view-tab ${activeView === 'chat' ? 'active' : ''}`}
          onClick={() => setActiveView('chat')}
        >
          Chat
        </button>
        <button
          type="button"
          className={`view-tab ${activeView === 'insights' ? 'active' : ''}`}
          onClick={() => setActiveView('insights')}
        >
          Execution Insights
        </button>
      </div>
      )}

      {activeView === 'chat' ? (
        <>
          {/* Pipeline Settings */}
          {!isAssistant && (
          <TestGraphSettings
            settings={settings}
            onSettingsChange={handleSettingsChange}
            collapsed={settingsCollapsed}
            onToggleCollapse={() => setSettingsCollapsed(!settingsCollapsed)}
            graphId={graphId}
            workspaceId={settings.workspace_id}
            cacheInfo={cacheInfo}
            onCacheStateChange={refreshCacheStatus}
          />
          )}

          {/* Session Context SNN Panel */}
          {sessionContextEnabled && sessionContextSnapshot && (
            <SessionContextPanel snapshot={sessionContextSnapshot} />
          )}

          {/* Live Evaluation Progress */}
          {activeExecutionId && (
            <div className="test-graph-live-progress">
              <LiveEvaluationProgress
                executionId={activeExecutionId}
                wsBaseUrl={getGraphBuilderBaseUrl().replace(/^http/, 'ws')}
                onComplete={() => setActiveExecutionId(null)}
              />
            </div>
          )}

          {/* Chat Interface */}
          <TestGraphChat
            graphId={graphId}
            messages={messages}
            onSendMessage={handleSendMessage}
            conversationHistory={conversationHistory}
            onViewInsight={handleViewInsight}
            onExplain={setExplainMessage}
            onSelectRouterGraph={handleRouterGraphSelection}
            welcomeMessage={isAssistant ? assistantPublicConfig?.welcome_message : null}
            suggestedQuestions={isAssistant ? assistantPublicConfig?.suggested_questions : null}
            routerTargets={isAssistant ? assistantPublicConfig?.router_targets : null}
            selectedGraphIds={selectedGraphIds}
            onGraphSelectionChange={isAssistant && assistantPublicConfig?.router_targets?.length ? setSelectedGraphIds : null}
          />
        </>
      ) : (
        <ExecutionInsightsTab graphId={graphId} focusInsightId={focusInsightId} onFocusConsumed={() => setFocusInsightId(null)} />
      )}

      {/* Explainability Drawer */}
      {explainMessage && (
        <ExplainabilityDrawer
          message={explainMessage}
          onClose={() => setExplainMessage(null)}
          onViewFullTrace={handleViewInsight}
          graphId={graphId}
          settings={settings}
        />
      )}

      {/* Publish Chat Agent modal */}
      {showPublishModal && (
        <div className="ca-publish-overlay" onClick={() => !publishing && setShowPublishModal(false)}>
          <div className="ca-publish-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Publish Chat Agent</h2>
            <p className="ca-modal-subtitle">
              Snapshot the current Test Graph configuration as a public chat assistant.
              The settings and billing account are locked server-side.
            </p>

            {publishError && (
              <div className="error-message" style={{ marginBottom: 14 }}>{publishError}</div>
            )}

            {publishResult ? (
              <div className="ca-publish-success">
                <strong>Published.</strong> Share or embed this URL:
                <div className="ca-success-url">
                  <input type="text" value={publishResult.url} readOnly />
                  <button
                    className="btn-secondary"
                    onClick={() => navigator.clipboard?.writeText(publishResult.url)}
                  >
                    Copy
                  </button>
                </div>
                <div className="ca-publish-actions" style={{ marginTop: 14 }}>
                  <button className="btn-secondary" onClick={() => setShowPublishModal(false)}>Close</button>
                  <button className="btn-primary" onClick={() => navigate('/user-home/chat-assistant')}>
                    Go to Chat Assistants
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="ca-field">
                  <label>Name</label>
                  <input
                    type="text"
                    value={publishForm.name}
                    onChange={(e) => setPublishForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="Assistant name"
                  />
                </div>
                <div className="ca-field">
                  <label>Description</label>
                  <textarea
                    rows={2}
                    value={publishForm.description}
                    onChange={(e) => setPublishForm((f) => ({ ...f, description: e.target.value }))}
                    placeholder="Internal description (optional)"
                  />
                </div>
                <div className="ca-field">
                  <label>Chat title</label>
                  <input
                    type="text"
                    value={publishForm.title}
                    onChange={(e) => setPublishForm((f) => ({ ...f, title: e.target.value }))}
                    placeholder="Shown in the chat header"
                  />
                </div>
                <div className="ca-field">
                  <label>Welcome message</label>
                  <textarea
                    rows={2}
                    value={publishForm.welcome_message}
                    onChange={(e) => setPublishForm((f) => ({ ...f, welcome_message: e.target.value }))}
                    placeholder="First message shown to the user (optional)"
                  />
                </div>
                <div className="ca-field">
                  <label>Suggested questions <span className="ca-hint">(one per line)</span></label>
                  <textarea
                    rows={3}
                    value={publishForm.suggested_questions}
                    onChange={(e) => setPublishForm((f) => ({ ...f, suggested_questions: e.target.value }))}
                    placeholder={'How do I appeal a CO-50 denial?\nWhat documents are required?'}
                  />
                </div>
                <div className="ca-publish-actions">
                  <button className="btn-secondary" onClick={() => setShowPublishModal(false)} disabled={publishing}>
                    Cancel
                  </button>
                  <button className="btn-primary" onClick={handlePublishChatAgent} disabled={publishing || !publishForm.name}>
                    {publishing ? 'Publishing...' : 'Publish'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default TestGraphPage;
